import React, { useEffect, useRef, useState } from "react";
import {
  useModalScope,
  useShortcut,
} from "../../../components/shortcuts/ShortcutProvider";
import {
  sceneById,
  sceneNeedsTheFloor,
  Want,
  wantById,
} from "../../../game/story";
import { StoryTracker } from "../../../sim/singletons/StoryTracker";
import { classNames } from "../../../utils/classNames";
import { PLAYER_PORTRAIT, WIFE_PORTRAIT } from "../../../utils/uiImages";
import {
  CutsceneDirector,
  StoryLine,
} from "../../scenes/story/CutsceneDirector";
import { TRIP_TRANSITIONS_DISABLED } from "../../scenes/TripTheater";
import { useGame, useShellVersion } from "../../useShell";
import { PhoneListing } from "./PhoneListing";

/**
 * The story on screen: the letterbox that frames a scene she walks
 * into, and the box the lines are read in. The CutsceneDirector decides
 * which line is up; this draws it and hands the player's press back.
 *
 * Two sizes of scene, one rule for the keys. A scene she is in is a
 * cutscene: black bars slide in from the top and bottom edges the
 * moment it begins and slide back out when it ends — the film's way of
 * saying the controls are off — while the floor underneath stays lit,
 * because her walk is the thing to watch. A thought alone frames
 * nothing: its box comes up where he is (over the bench view, as
 * often as not) and goes back down. Either way the box claims the
 * modal scope, so every floor key is dead under it and the clock is
 * stopped (the tracker's hold), and the same press serves both: one
 * finishes the line being written out, the next turns to the next
 * line. A scene can't be skipped, only read quickly.
 *
 * Motion is tied to what changes, so reading quickly never stutters:
 * the box rises once when a speaker starts and drops once when the
 * lines stop (a new speaker's box rises in its place); between lines
 * of the same speaker only the words change. The phone she holds up
 * fills the screen above the box for its line — nearly the height of
 * the window, the way the thing she wants fills the room — and drops
 * away with it.
 */
export const StoryLayer: React.FC = () => {
  const game = useGame();
  useShellVersion();
  const director = game.entities.tryGetSingleton(CutsceneDirector);
  const sceneId = game.entities.tryGetSingleton(StoryTracker)?.playing ?? null;
  const cutscene = sceneId !== null && sceneNeedsTheFloor(sceneById(sceneId));
  const line = director?.currentLine() ?? null;
  const box = useLingering(line, instantMotion() ? 0 : BOX_DROP_MS);
  const phone = useLingering(
    line?.beat.kind === "phone" ? line.beat.want : null,
    instantMotion() ? 0 : PHONE_DROP_MS,
  );
  return (
    <>
      <Letterbox open={cutscene} />
      {phone && (
        <PhoneStage want={wantById(phone.value)} leaving={phone.leaving} />
      )}
      {box && director && (
        <StoryTextBox
          key={voiceOf(box.value)}
          line={box.value}
          leaving={box.leaving}
          onAdvance={() => director.advance()}
        />
      )}
    </>
  );
};

/** How long the box takes to drop away once the lines stop (index.css). */
const BOX_DROP_MS = 150;
/** How long the phone takes to drop once her line is read (index.css). */
const PHONE_DROP_MS = 200;
/** Characters per second the line is written out at. */
const REVEAL_RATE = 45;

type Voice = "wife" | "thought";

function voiceOf(line: StoryLine): Voice {
  return line.beat.kind === "think" ? "thought" : "wife";
}

/** Motion the build or the player has asked not to see is cut, not played. */
function instantMotion(): boolean {
  return (
    TRIP_TRANSITIONS_DISABLED ||
    (typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches)
  );
}

/**
 * A value that stays on screen a moment after it goes: while `value` is
 * set it is returned as-is; once it clears, the last one is returned
 * with `leaving` for `ms` more so its exit can play, then nothing. A
 * new value inside that window takes over at once. With `ms` at zero
 * the exit is skipped.
 */
function useLingering<T>(
  value: T | null,
  ms: number,
): { value: T; leaving: boolean } | null {
  const last = useRef<T | null>(null);
  const [gone, setGone] = useState(true);
  if (value !== null) last.current = value;
  useEffect(() => {
    if (value !== null || ms <= 0) {
      setGone(value === null);
      return;
    }
    const timer = setTimeout(() => setGone(true), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  if (value !== null) return { value, leaving: false };
  if (ms > 0 && !gone && last.current !== null) {
    return { value: last.current, leaving: true };
  }
  return null;
}

/**
 * The cutscene's frame: a bar off each of the top and bottom edges,
 * slid in while a scene she walks into plays. Always mounted so the
 * slide runs both ways (index.css owns the motion); `data-open` is the
 * state, for the CSS and the specs alike.
 */
const Letterbox: React.FC<{ open: boolean }> = ({ open }) => (
  <div
    aria-hidden
    data-testid="story-letterbox"
    data-open={open}
    className="story-letterbox pointer-events-none fixed inset-0 z-[55]"
  >
    <div className="story-letterbox-bar absolute inset-x-0 top-0" />
    <div className="story-letterbox-bar absolute inset-x-0 bottom-0" />
  </div>
);

/** Holds the modal scope for as long as it is mounted. */
const ModalClaim: React.FC = () => {
  useModalScope();
  return null;
};

const StoryTextBox: React.FC<{
  line: StoryLine;
  /** The lines have stopped: the box is on its way down and takes no press. */
  leaving: boolean;
  onAdvance: () => void;
}> = ({ line, leaving, onAdvance }) => {
  const text = line.beat.text;
  const lineKey = `${line.sceneId}-${line.index}`;
  const instant = instantMotion();
  // How far the line has been written out, remembered per line so a
  // new line starts from nothing on the very render it arrives.
  const [typed, setTyped] = useState({ key: lineKey, shown: 0 });
  const shown = instant ? text.length : typed.key === lineKey ? typed.shown : 0;
  const complete = shown >= text.length;
  const frame = useRef(0);
  const onAdvanceRef = useRef(onAdvance);
  onAdvanceRef.current = onAdvance;

  useEffect(() => {
    if (instant) return;
    let count = 0;
    let last = performance.now();
    const tick = (now: number) => {
      count += ((now - last) / 1000) * REVEAL_RATE;
      last = now;
      const next = Math.min(text.length, Math.floor(count));
      setTyped({ key: lineKey, shown: next });
      if (next < text.length) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [lineKey, text, instant]);

  const press = () => {
    if (leaving) return;
    if (!complete) {
      // Finish the line in one go — and stop the writer, or its next
      // frame would take the words back to where it had got to.
      cancelAnimationFrame(frame.current);
      setTyped({ key: lineKey, shown: text.length });
      return;
    }
    onAdvanceRef.current();
  };
  useShortcut("advance-dialogue", press);

  const thought = line.beat.kind === "think";

  return (
    <>
      {!leaving && <ModalClaim />}
      <div
        className="pointer-events-auto fixed inset-x-0 bottom-8 z-[56] flex flex-col items-center gap-4 px-6"
        onClick={press}
        role="presentation"
      >
        <div
          role="dialog"
          aria-live="polite"
          aria-label={thought ? "A thought" : "Wife"}
          data-testid="story-box"
          data-voice={thought ? "thought" : "wife"}
          data-complete={complete}
          data-leaving={leaving}
          className={classNames(
            "story-box-rise flex w-full max-w-2xl items-stretch gap-4",
            thought
              ? "paper-note pencil-hand min-h-[7.5rem] px-5 py-4"
              : "hud-chip min-h-[7.5rem] border-paper-manila/30 px-5 py-4",
          )}
        >
          <Portrait who={thought ? "player" : "wife"} />
          <div className="flex min-w-0 flex-1 flex-col">
            {!thought && (
              <div className="mb-1 font-condensed text-[0.7rem] font-bold uppercase tracking-[0.25em] text-gold">
                Wife
              </div>
            )}
            <p
              className={classNames(
                "flex-1 leading-snug",
                thought
                  ? "text-[1.25rem] text-ink-pencil"
                  : "font-condensed text-[1.2rem] text-paper-manila",
              )}
              data-testid="story-line"
            >
              {text.slice(0, shown)}
              {/* The rest of the line, invisible, so the box never grows as
                the words arrive */}
              <span className="invisible">{text.slice(shown)}</span>
            </p>
            <div className="flex h-4 items-end justify-end">
              {complete && !leaving && (
                <span
                  aria-hidden
                  className={classNames(
                    "story-caret block size-0 border-x-[0.4rem] border-t-[0.5rem] border-x-transparent",
                    thought ? "border-t-ink-pencil" : "border-t-paper-manila",
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/** The listing's height over its width, at any size (PhoneListing
 * scales everything with its width, so this is what the layout holds). */
const PHONE_ASPECT = 1.6;
/** Clear space above the phone, in rem. */
const PHONE_TOP_REM = 1.5;
/** The room the box keeps at the bottom, in rem — a little less than
 * the box and its margin take, so the phone's bottom edge runs behind
 * the box rather than stopping short of it. */
const PHONE_BOTTOM_REM = 8;

/**
 * Her phone, held up: the listing drawn as tall as the window allows
 * above the box, at the phone's own proportions, and never wider than
 * the window can hold. Sized in pixels because PhoneListing scales its
 * type from its width.
 */
const PhoneStage: React.FC<{ want: Want; leaving: boolean }> = ({
  want,
  leaving,
}) => {
  const { width: vw, height: vh } = useViewportSize();
  const rem = rootFontSize();
  const room = vh - (PHONE_TOP_REM + PHONE_BOTTOM_REM) * rem;
  const width = Math.max(160, Math.min(room / PHONE_ASPECT, vw * 0.6));
  return (
    <div
      aria-hidden
      data-leaving={leaving}
      className="story-phone-rise pointer-events-none fixed inset-x-0 z-[56] flex justify-center"
      style={{ top: `${PHONE_TOP_REM}rem` }}
    >
      <PhoneListing want={want} width={width} />
    </div>
  );
};

function rootFontSize(): number {
  if (typeof document === "undefined") return 16;
  return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
}

function useViewportSize(): { width: number; height: number } {
  const read = () => ({ width: window.innerWidth, height: window.innerHeight });
  const [size, setSize] = useState(read);
  useEffect(() => {
    const onResize = () => setSize(read());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return size;
}

/**
 * The speaker's face beside the line. A missing picture (the art isn't
 * drawn yet) leaves an empty frame rather than a broken image.
 */
const Portrait: React.FC<{ who: "wife" | "player" }> = ({ who }) => {
  const [failed, setFailed] = useState(false);
  return (
    <div
      className={classNames(
        "flex size-24 shrink-0 items-center justify-center overflow-hidden rounded-sm",
        who === "wife"
          ? "border border-paper-manila/30 bg-workshop-panel"
          : "border border-ink-black/20 bg-paper-cream",
      )}
      data-testid={`story-portrait-${who}`}
    >
      {!failed && (
        <img
          src={who === "wife" ? WIFE_PORTRAIT : PLAYER_PORTRAIT}
          alt=""
          decoding="sync"
          className="size-full object-cover [image-rendering:pixelated]"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
};
